import { useMemo, useState } from 'react';
import { Trash2, ArrowUpDown } from 'lucide-react';
import DatePicker from 'react-datepicker';
import { primaryBankAccountName } from '../../../../../helpers/constants';
import { getTranslatedMonth } from '../../../../../helpers/localization';
import { arrayToCommaSeparatedString, formatCurrency } from './helper';
import useLocalization from '../../../../../hooks/useLocalization';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../ui/select';
import { Button } from '../../../../ui/button';

const CATEGORY_BADGE_THEME = {
  Cash: { solid: '#059669', lightBg: '#d1fae5' },
  Stocks: { solid: '#7c3aed', lightBg: '#ede9fe' },
  'Real Estate': { solid: '#ea580c', lightBg: '#ffedd5' },
  Pension: { solid: '#475569', lightBg: '#e2e8f0' },
  'Fixed Income': { solid: '#0d9488', lightBg: '#ccfbf1' },
  Anona: { solid: '#db2777', lightBg: '#fce7f3' },
  RSU: { solid: '#4338ca', lightBg: '#e0e7ff' },
  Other: { solid: '#64748b', lightBg: '#f1f5f9' },
  Liability: { solid: '#b45309', lightBg: '#fef3c7' },
  'One Off Change': { solid: '#a855f7', lightBg: '#f3e8ff' }
};

const getCategoryBadgeTheme = category => CATEGORY_BADGE_THEME[category] ?? CATEGORY_BADGE_THEME.Other;

const TX_COLOR = {
  Start: 'success',
  End: 'danger',
  Buy: 'success',
  Sell: 'danger',
  Add: 'success',
  Subtract: 'danger',
  Set: 'primary'
};

const TX_SIGN = {
  Start: '+',
  End: '-',
  Buy: '+',
  Sell: '-',
  Add: '+',
  Subtract: '-',
  Set: null
};
const txTypes = ['All', 'Start', 'End', 'Buy', 'Sell', 'Add', 'Subtract', 'Set'];
const TYPE_FILTERS = ['All', 'Asset', 'Liability', 'One Off Change'];

const TX_TYPE_KEY = { All: 'all', Start: 'start', End: 'end', Buy: 'buy', Sell: 'sell', Add: 'add', Subtract: 'subtract', Set: 'set' };
const TYPE_FILTER_KEY = { All: 'all', Asset: 'asset', Liability: 'liability', 'One Off Change': 'one_off_change' };
const CATEGORY_KEY = {
  Cash: 'cash',
  Stocks: 'stocks',
  'Real Estate': 'real_estate',
  Pension: 'pension',
  'Fixed Income': 'fixed_income',
  Anona: 'anona',
  RSU: 'rsu',
  Other: 'other',
  Liability: 'liability',
  'One Off Change': 'one_off_change'
};

const buildAssetActions = (assets, startYear) =>
  assets
    .filter(a => a.name !== primaryBankAccountName && a.category !== 'Fixed Income')
    .flatMap(asset => {
      const base = {
        _id: asset._id,
        category: asset.category,
        name: asset.name,
        type: 'Asset',
        isEditMode: asset.editMode,
        sortingPrecedence: 1
      };
      const actions = [];
      if (asset.editMode || asset.buyingYear !== startYear || asset.buyingMonth !== 0) {
        actions.push({
          ...base,
          year: asset.buyingYear,
          month: asset.buyingMonth,
          transactionType: 'Buy',
          value: asset.value
        });
      }
      if (asset.sellingYear) {
        actions.push({
          ...base,
          year: asset.sellingYear,
          month: asset.sellingMonth,
          transactionType: 'Sell',
          value: asset.soldValue
        });
      }
      return actions;
    });

const buildFixedIncomeActions = (assets, startYear) =>
  assets
    .filter(a => a.category === 'Fixed Income')
    .flatMap(asset => {
      const base = {
        _id: asset._id,
        category: asset.category,
        name: asset.name,
        type: 'Asset',
        isEditMode: asset.editMode,
        sortingPrecedence: 1
      };
      const actions = [];
      if (asset.editMode || asset.startYear !== startYear || asset.startMonth !== 0) {
        actions.push({
          ...base,
          year: asset.startYear,
          month: asset.startMonth,
          transactionType: 'Start',
          value: asset.value
        });
      }
      if (asset.endYear) {
        actions.push({
          ...base,
          year: asset.endYear,
          month: asset.endMonth,
          transactionType: 'End',
          value: asset.endValue
        });
      }
      return actions;
    });

const buildLiabilityActions = (liabilities, startYear) =>
  liabilities.flatMap(liability => {
    const base = {
      _id: liability._id,
      category: 'Liability',
      name: liability.name,
      type: 'Liability',
      isEditMode: liability.editMode,
      sortingPrecedence: 1,
      relatedAssets: liability.relatedAssets
    };
    const actions = [];
    if (liability.editMode || liability.startYear !== startYear || liability.startMonth !== 0) {
      actions.push({
        ...base,
        year: liability.startYear,
        month: liability.startMonth,
        transactionType: 'Start',
        value: liability.value
      });
    }
    if (liability.endYear) {
      actions.push({
        ...base,
        year: liability.endYear,
        month: liability.endMonth,
        transactionType: 'End',
        value: liability.endValue
      });
    }
    return actions;
  });

const buildOneOffActions = (oneOffChanges, assets, liabilities) => {
  return oneOffChanges
    .filter(o => o._id)
    .map(o => {
      const related =
        o.type === 'Asset' ? assets.find(a => a._id === o.refId) : liabilities.find(l => l._id === o.refId);
      return {
        ...o,
        name: related?.name,
        category: 'One Off Change',
        isOneOffChange: true,
        sortingPrecedence: 0.01 + o.month * 0.01
      };
    });
};

const createTimeline = strategy => {
  const { initialYear, assets = [], liabilities = [], oneOffChanges = [] } = strategy;

  const timeline = [
    ...buildAssetActions(assets, initialYear),
    ...buildFixedIncomeActions(assets, initialYear),
    ...buildLiabilityActions(liabilities, initialYear),
    ...buildOneOffActions(oneOffChanges, assets, liabilities)
  ];
  const sorted = timeline.sort((a, b) =>
    a.year === b.year
      ? a.month - a.sortingPrecedence - (b.month - b.sortingPrecedence)
      : a.year - a.sortingPrecedence - (b.year - b.sortingPrecedence)
  );

  const yearlyBankBalances = assets.find(a => a.name === primaryBankAccountName)?.yearlyValues ?? [];
  let currentBalance = {};

  return sorted.map(action => {
    if (currentBalance.year !== action.year) {
      currentBalance = {
        year: action.year,
        value: yearlyBankBalances.find(b => b.year === action.year)?.valueBeforeTransactions ?? 0
      };
    }
    if (action.type === 'Asset' && action.isEditMode) {
      currentBalance.value += action.transactionType === 'Buy' ? -parseInt(action.value) : parseInt(action.value);
    }
    return { ...action, primaryBankBalance: currentBalance.value };
  });
};

const ActionSummaryView = ({ strategy, onCancelAction }) => {
  const { translate, isRTL } = useLocalization();
  const timeline = useMemo(() => createTimeline(strategy), [strategy]);
  const [filterType, setFilterType] = useState('All');
  const [filterTx, setFilterTx] = useState('All');
  const [sortDesc, setSortDesc] = useState(false);
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });

  const filtered = useMemo(() => {
    const { startDate, endDate } = dateRange;
    const result = timeline
      .filter(a => {
        if (filterType === 'One Off Change') return a.isOneOffChange;
        if (filterType !== 'All') return a.type === filterType && !a.isOneOffChange;
        return true;
      })
      .filter(a => filterTx === 'All' || (a.transactionType || a.action) === filterTx)
      .filter(a => {
        if (!startDate && !endDate) return true;
        const actionDate = new Date(a.year, a.month);
        if (startDate && !endDate) return actionDate >= new Date(startDate.getFullYear(), startDate.getMonth());
        if (startDate && endDate) {
          return (
            actionDate >= new Date(startDate.getFullYear(), startDate.getMonth()) &&
            actionDate <= new Date(endDate.getFullYear(), endDate.getMonth())
          );
        }
        return true;
      });
    return sortDesc ? [...result].reverse() : result;
  }, [timeline, filterType, filterTx, sortDesc, dateRange]);

  const groupedByMonth = useMemo(() => {
    const groups = [];
    for (const action of filtered) {
      const matchingGroup = groups.find(g => g.year === action.year && g.month === action.month);
      if (matchingGroup) {
        matchingGroup.actions.push(action);
      } else {
        groups.push({ year: action.year, month: action.month, actions: [action] });
      }
    }
    return groups;
  }, [filtered]);

  if (timeline.length === 0) {
    return <p className="text-muted text-center p-5">{translate('nothing_to_show')}</p>;
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">{translate('type')}</span>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="h-8 px-3 text-sm w-[150px] shadow-none border-gray-200 rounded-md">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[9999] bg-white shadow-md rounded-md">
              {TYPE_FILTERS.map(t => (
                <SelectItem key={t} value={t}>
                  {translate(TYPE_FILTER_KEY[t])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-px h-5 bg-gray-200" />

        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">{translate('action')}</span>
          <Select value={filterTx} onValueChange={setFilterTx}>
            <SelectTrigger className="h-8 px-3 text-sm w-[120px] shadow-none border-gray-200 rounded-md">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-[9999] bg-white shadow-md rounded-md">
              {txTypes.map(t => (
                <SelectItem key={t} value={t}>
                  {translate(TX_TYPE_KEY[t])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-px h-5 bg-gray-200" />

        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">{translate('date')}</span>
          <div style={{ width: 170 }}>
            <DatePicker
              selectsRange
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={([start, end]) => setDateRange({ startDate: start, endDate: end })}
              dateFormat="MM/yyyy"
              showMonthYearPicker
              isClearable
              placeholderText={translate('all_dates')}
              className="border border-gray-200 rounded-md w-100 text-sm px-3 bg-transparent outline-none h-8"
              wrapperClassName="w-100"
              popperProps={{ strategy: 'fixed' }}
              popperClassName="z-[9999]"
            />
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          className={`${isRTL ? 'mr-auto' : 'ml-auto'} gap-1 text-muted bg-gray-200 border-none hover:bg-gray-300`}
          onClick={() => setSortDesc(d => !d)}
        >
          <ArrowUpDown className="h-3.5 w-3.5" />
          {sortDesc ? translate('newest_first') : translate('oldest_first')}
        </Button>
      </div>

      <p className="text-muted mb-3" style={{ fontSize: 12 }}>
        {translate(filtered.length === 1 ? 'n_action' : 'n_actions', { count: filtered.length })}
      </p>

      {filtered.length === 0 ? (
        <p className="text-muted text-center py-4">{translate('nothing_to_show')}</p>
      ) : (
        <div style={{ position: 'relative', ...(isRTL ? { paddingRight: 28 } : { paddingLeft: 28 }) }}>
          {groupedByMonth.map((group, groupIndex) => (
            <div key={`${group.year}-${group.month}`}>
              {groupIndex > 0 && (
                <div
                  role="separator"
                  aria-hidden
                  style={{
                    margin: '20px 0 16px',
                    height: 1,
                    background: '#e5e7eb',
                    borderRadius: 1
                  }}
                />
              )}
              <div
                style={{
                  marginBottom: 10,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  color: '#6b7280'
                }}
              >
                {getTranslatedMonth(translate, group.month)} {group.year}
              </div>

              {group.actions.map((action, index) => {
                const txType = action.transactionType || action.action;
                const color = TX_COLOR[txType] || 'primary';
                const solidColor = { success: '#16a34a', danger: '#dc2626', primary: '#2563eb' }[color] || '#2563eb';
                const badgeTheme = getCategoryBadgeTheme(action.category);

                const detail =
                  action.isOneOffChange && action.description
                    ? action.description
                    : action.type === 'Liability' && action.relatedAssets?.length > 0
                    ? `${translate('linked_asset')}: ${arrayToCommaSeparatedString(action.relatedAssets)}`
                    : null;

                const txSign = TX_SIGN[txType];

                return (
                  <div
                    key={`${group.year}-${group.month}-${action._id ?? 'row'}-${index}`}
                    style={{ position: 'relative', marginBottom: 10 }}
                  >
                    <div
                      aria-hidden
                      style={{
                        position: 'absolute',
                        ...(isRTL ? { right: -18 } : { left: -18 }),
                        top: 0,
                        height: index < group.actions.length - 1 ? 'calc(100% + 10px)' : '100%',
                        width: 2,
                        background: '#dee2e6',
                        borderRadius: 2,
                        zIndex: 0,
                        pointerEvents: 'none'
                      }}
                    />
                    {/* dot */}
                    <div
                      style={{
                        position: 'absolute',
                        ...(isRTL ? { right: -26 } : { left: -26 }),
                        top: 10,
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        background: solidColor,
                        border: '2px solid #fff',
                        boxShadow: `0 0 0 2px ${solidColor}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: 13,
                        fontWeight: 700,
                        lineHeight: 1,
                        zIndex: 1
                      }}
                    >
                      {txSign}
                    </div>

                    <div
                      className="group"
                      style={{
                        position: 'relative',
                        zIndex: 1,
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                        padding: '10px 16px',
                        minHeight: '72px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: 6
                      }}
                    >
                      {/* Row 1: category · detail · delete */}
                      <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                        <div className="d-flex align-items-center gap-2">
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              padding: '1px 7px',
                              borderRadius: 20,
                              background: badgeTheme.lightBg,
                              color: badgeTheme.solid,
                              letterSpacing: 0.3,
                              textTransform: 'uppercase',
                              flexShrink: 0
                            }}
                          >
                            {translate(CATEGORY_KEY[action.category] ?? action.category)}
                          </span>
                        </div>
                        {detail && (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: '#6b7280',
                              flex: 1,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              textAlign: 'center'
                            }}
                          >
                            {detail}
                          </span>
                        )}

                        {onCancelAction && (
                          <button
                            type="button"
                            title="Remove this action"
                            onClick={() => onCancelAction(action)}
                            className="inline-flex shrink-0 items-center justify-center cursor-pointer border-0 bg-transparent p-0 opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto"
                          >
                            <Trash2 size={14} strokeWidth={2} color="#ef4444" />
                          </button>
                        )}
                      </div>

                      {/* Row 2: name · value */}
                      <div className="d-flex align-items-center justify-content-between gap-2">
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: '#111827',
                            lineHeight: 1.4,
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {action.name}
                        </span>
                        {action.value != null && (
                          <span style={{ fontSize: 13, fontWeight: 700, color: solidColor, flexShrink: 0 }}>
                            {!isRTL && action.value !== 0 ? txSign : ''}
                            {formatCurrency(action.value)}
                            {isRTL && action.value !== 0 ? txSign : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActionSummaryView;
